import { Typography } from "@/components/atoms";
import { useLayout } from "@/hooks";
import { useAppSelector } from "@/redux/hooks";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { ClientPortalBannerPhase } from "@/redux/slices/operator/types";
import { clsx } from "@/utils";

/**
 * Client Portal banner for OPERATORS (DEV-1486).
 *
 * Two messages, one replacing the other, driven by a single phase so both can never show at once:
 * the release is coming, then it has landed, then nothing.
 *
 * Worded for operators — what their clients are about to see, plus the Client Manager they get
 * themselves. The client-facing halves live on the legacy PHP pages (before the swap) and in the new
 * client portal (after it), and all three read the same phase.
 *
 * The "learn more" link is omitted while the URL is unset — the page may not be published yet, and an
 * announcement without a link is still worth showing.
 *
 * Left padding tracks the sidebar the same way VehicleMigrationBanner does, so the message stays
 * centred on the content rather than sliding under the nav when it collapses.
 */
const MESSAGE: Record<Exclude<ClientPortalBannerPhase, "">, { lead: string; body: string }> = {
  announcement: {
    lead: "COMING 4 SEP:",
    body: "New Client Manager, new booking flow, and a redesigned Web App for your clients & EAs.",
  },
  /*
   * Operator wording, because this banner only ever renders in the operator dashboard. The client
   * half of both phases lives on the legacy pages (see client-portal-announcement.phtml) and reads
   * "Your new booking experience is here — redesigned for clients, EAs, and travel managers."
   *
   * Only the lead changes between the two operator phases: the same three things are coming and then
   * have arrived, so saying it twice over in different words would read as two separate releases.
   */
  live: {
    lead: "NOW LIVE:",
    body: "New Client Manager, new booking flow, and a redesigned Web App for your clients & EAs.",
  },
};

export const ClientPortalAnnouncementBanner = () => {
  /*
   * Read the operator off the selector, NOT `useOperator()` — that hook THROWS when there is no
   * active operator, and this component is the first thing `Private` renders, above every other
   * banner and outside any error boundary.
   *
   * There is a real window where the operator is missing: AccountProvider only shows its loader on
   * the INITIAL mount, so during a later refetch — an FA admin switching between operators, for
   * instance — the tree keeps rendering while `activeOperator` is briefly undefined. A throw there
   * takes down the whole dashboard.
   *
   * An announcement must never be able to do that. Same rule as the PHP half: fail to nothing.
   */
  const announcement = useAppSelector(activeOperatorSelector)?.announcement;
  const {
    layout: { sidebarOpen },
  } = useLayout();

  const phase = announcement?.clientPortalPhase;
  if (!phase) return null;

  const { lead, body } = MESSAGE[phase];

  return (
    <div className="relative top-0 z-40 bg-vivid-purple-dark px-5 py-2 text-white transition-all duration-200">
      <div
        className={clsx(
          "mx-auto flex max-w-screen-2xl flex-row flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center transition-all duration-200",
          sidebarOpen ? "lg:pl-[250px]" : "lg:pl-[65px]"
        )}
      >
        {/*
         * `paragraph` outside, `action` on the lead: only the lead is meant to be bold. The two
         * variants differ in weight alone (both 14px), so nesting them is what creates the contrast —
         * wrapping the lead in `font-semibold` while the parent was already `action` did nothing.
         *
         * Kept as one element rather than three siblings so the sentence still wraps mid-line: the
         * container is a flex row with `gap-x-2`, and separate children would break at the lead/body
         * boundary and put a gap there.
         */}
        <Typography variant="paragraph" className="text-white">
          ✨{" "}
          <Typography variant="action" className="text-white">
            {lead}
          </Typography>{" "}
          {body} ✨
        </Typography>
        {announcement.clientPortalUrl && (
          <a
            href={announcement.clientPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 whitespace-nowrap text-white underline"
          >
            <Typography variant="action" className="text-white">
              Click here to learn more →
            </Typography>
          </a>
        )}
      </div>
    </div>
  );
};
